import { type Subject } from "@core/grades/types";
import { cn } from "@design-system/lib/utils";
import { BookOpenIcon } from "lucide-react";

interface SubjectSelectionProps {
  subjects: Subject[];
  activeSubject: Subject;
  onSubjectChange: (subject: Subject) => void;
  className?: string;
}

export function SubjectSelection({
  subjects,
  activeSubject,
  onSubjectChange,
  className,
}: SubjectSelectionProps) {
  if (!subjects?.length) return null;

  return (
    <div className={cn("relative", className)}>
      {/* Subject Tabs */}
      <div className="flex justify-center">
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 lg:gap-8">
          {subjects.map((subject) => {
            const isActive = subject.id === activeSubject.id;

            return (
              <button
                key={subject.id}
                onClick={() => onSubjectChange(subject)}
                className={cn(
                  "group flex flex-col items-center gap-3 transition-all duration-300",
                  "hover:scale-105",
                )}
              >
                {/* Circular Image/Icon */}
                <div className="relative">
                  <div
                    className={cn(
                      "flex h-20 w-20 items-center justify-center rounded-full transition-all duration-300 sm:h-24 sm:w-24",
                      "border-2 border-transparent",
                      isActive
                        ? "border-primary"
                        : "border-gray-200 group-hover:border-gray-300 dark:border-gray-700 dark:group-hover:border-gray-600",
                    )}
                  >
                    {subject.image?.url ? (
                      <img
                        src={subject.image.url}
                        alt={subject.name}
                        className="h-12 w-12 rounded-full object-cover sm:h-14 sm:w-14"
                      />
                    ) : (
                      <BookOpenIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 sm:h-14 sm:w-14" />
                    )}
                  </div>

                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 h-3 w-3 -translate-x-1/2 rounded-full bg-primary" />
                  )}
                </div>

                {/* Subject Name */}
                <span
                  className={cn(
                    "max-w-[80px] text-center text-sm font-medium transition-colors duration-300 sm:max-w-[100px] sm:text-base",
                    isActive
                      ? "text-primary"
                      : "text-gray-600 group-hover:text-gray-800 dark:text-gray-300 dark:group-hover:text-gray-100",
                  )}
                >
                  {subject.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Speech Bubble Arrow */}
      <div className="absolute left-1/2 top-full -translate-x-1/2">
        <div className="h-0 w-0 border-l-[12px] border-r-[12px] border-t-[12px] border-l-transparent border-r-transparent border-t-gray-100 dark:border-t-gray-800" />
      </div>
    </div>
  );
}
